import React, { useEffect, useMemo, useRef } from "react"
import Icon from "../../Icon"
import { formatDateInputValue } from "../../../utils/format"
import { GROUP_THEMES, GROUP_TYPES } from "../../../utils/groupTheme"
import { Shield, Users, Pencil } from "lucide-react"

const NON_RECURRING_OPTIONS = [
  "Wedding Contribution",
  "Burial/Funeral Planning",
  "Travel Or Vacation Fund",
  "Medical Fundraiser",
  "Other",
]

const RECURRING_OPTIONS = [
  "Chamaa (Merry-Go-Round)",
  "Project Savings",
  "Welfare (Members only)",
]

const INVOICE_FREQUENCY_OPTIONS = [
  "Daily",
  "Bi-Daily",
  "Weekly",
  "Bi-Weekly",
  "Monthly",
  "Bi-Monthly",
  "Quarterly",
  "Bi-Annually",
  "Annually",
  "As Needed",
]

const CHAMAA_LABEL = "Chamaa (Merry-Go-Round)"
const PROJECT_SAVINGS_LABEL = "Project Savings"
const WELFARE_LABEL = "Welfare/Support group"
const CHAMAA_CYCLE_FREQUENCIES = INVOICE_FREQUENCY_OPTIONS.filter((option) => option !== "As Needed")

const RECIPIENT_OPTIONS = [
  { value: "Admin", label: "Admin (Group creator)" },
  { value: "Selected Member", label: "Selected Member (choose later)" },
  { value: "Bank", label: "Bank Account (future)" },
]

function StepEventType({
  category,
  nonRecurringValue,
  recurringValue,
  otherDescription,
  autoInvoicing,
  invoiceFrequency,
  invoiceStartDate,
  longAmount,
  chamaaMaxSharesPerMember,
  chamaaFirstCycleStartDate,
  chamaaFirstCycleInvoiceFrequency,
  projectSavingsPerPersonAmount,
  projectSavingsClosingDate,
  welfareRegistrationFee,
  welfareEnrollmentEnabled,
  welfareEnrollmentStartDate,
  welfareEnrollmentEndDate,
  welfareBenefitMember,
  welfareBenefitSpouse,
  welfareBenefitSpouseParent,
  welfareBenefitChild,
  welfareBenefitSibling,
  welfareBenefitParent,
  welfareReimbursementMember,
  welfareReimbursementSpouse,
  welfareReimbursementSpouseParent,
  welfareReimbursementChild,
  welfareReimbursementSibling,
  welfareReimbursementParent,
  welfareCoverParents,
  welfareCoverSpouse,
  welfareCoverSpouseParents,
  welfareCoverChildren,
  welfareCoverSiblings,
  welfareProbationValue,
  welfareProbationUnit,
  lateFee,
  recipient,
  validation = {},
  invalidAttemptCount = 0,
  onChangeCategory,
  onChangeNonRecurring,
  onChangeRecurring,
  onChangeOther,
  onChangeAutoInvoicing,
  onChangeInvoiceFrequency,
  onChangeInvoiceStartDate,
  onChangeLongAmount,
  onChangeChamaaMaxSharesPerMember,
  onChangeChamaaFirstCycleStartDate,
  onChangeChamaaFirstCycleInvoiceFrequency,
  onChangeProjectSavingsPerPersonAmount,
  onChangeProjectSavingsClosingDate,
  onChangeWelfareRegistrationFee,
  onChangeWelfareEnrollmentEnabled,
  onChangeWelfareEnrollmentStartDate,
  onChangeWelfareEnrollmentEndDate,
  onChangeWelfareBenefitMember,
  onChangeWelfareBenefitSpouse,
  onChangeWelfareBenefitSpouseParent,
  onChangeWelfareBenefitChild,
  onChangeWelfareBenefitSibling,
  onChangeWelfareBenefitParent,
  onChangeWelfareReimbursementMember,
  onChangeWelfareReimbursementSpouse,
  onChangeWelfareReimbursementSpouseParent,
  onChangeWelfareReimbursementChild,
  onChangeWelfareReimbursementSibling,
  onChangeWelfareReimbursementParent,
  onChangeWelfareCoverParents,
  onChangeWelfareCoverSpouse,
  onChangeWelfareCoverSpouseParents,
  onChangeWelfareCoverChildren,
  onChangeWelfareCoverSiblings,
  onChangeWelfareProbationValue,
  onChangeWelfareProbationUnit,
  onChangeLongFrequency,
  onChangeLateFee,
  onChangeRecipient,
}) {
  const recurringRef = useRef(null)
  const nonRecurringRef = useRef(null)
  const minDate = formatDateInputValue(new Date())

  useEffect(() => {
    if (recurringValue && recurringRef.current) {
      recurringRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [recurringValue])

  useEffect(() => {
    if (nonRecurringValue === "Other" && nonRecurringRef.current) {
      nonRecurringRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [nonRecurringValue])

  const isOnDemandFrequency = invoiceFrequency === "As Needed" || invoiceFrequency === "Is Needed"
  const isChamaaSelected =
    recurringValue === CHAMAA_LABEL || String(recurringValue || "").toLowerCase().includes("chamaa")
  const recurringSlug = String(recurringValue || "").trim().toLowerCase()
  const isProjectSavingsSelected =
    recurringValue === PROJECT_SAVINGS_LABEL ||
    recurringSlug.includes("project-savings") ||
    recurringSlug.includes("project savings")
  const isWelfareSelected =
    recurringValue === WELFARE_LABEL ||
    recurringSlug.includes("welfare") ||
    recurringSlug.includes("support-group")
  const projectSavingsPerPerson = Number(projectSavingsPerPersonAmount || 0)
  const parsedClosingDate = projectSavingsClosingDate ? new Date(`${projectSavingsClosingDate}T00:00:00`) : null
  const closingDateIsValid = parsedClosingDate && !Number.isNaN(parsedClosingDate.getTime())
  const today = new Date(`${minDate}T00:00:00`)
  const monthSpan =
    closingDateIsValid && parsedClosingDate.getTime() > today.getTime()
      ? Math.max(
          1,
          Math.ceil((parsedClosingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30))
        )
      : 0
  const projectSavingsMonthlyAmount =
    monthSpan > 0 && projectSavingsPerPerson > 0 ? projectSavingsPerPerson / monthSpan : 0
  const welfareProbationPreview = useMemo(() => {
    const probationUnit = String(welfareProbationUnit || "").trim().toLowerCase()
    const probationValue = Number(welfareProbationValue)
    const hasProbation =
      ["days", "months"].includes(probationUnit) &&
      Number.isInteger(probationValue) &&
      probationValue > 0
    if (!hasProbation) {
      return {
        enabled: false,
        startDateLabel: "",
        endDateLabel: "",
        message: "No probation period is configured.",
      }
    }

    let probationStartDate = null
    if (welfareEnrollmentEnabled) {
      if (welfareEnrollmentEndDate) {
        probationStartDate = new Date(`${welfareEnrollmentEndDate}T00:00:00`)
      }
    } else {
      probationStartDate = new Date(`${minDate}T00:00:00`)
    }

    if (!probationStartDate || Number.isNaN(probationStartDate.getTime())) {
      return {
        enabled: true,
        startDateLabel: "",
        endDateLabel: "",
        message: "Set enrollment end date to preview probation start and end dates.",
      }
    }

    const probationEndDate = new Date(probationStartDate)
    if (probationUnit === "days") {
      probationEndDate.setDate(probationEndDate.getDate() + probationValue)
    } else {
      probationEndDate.setMonth(probationEndDate.getMonth() + probationValue)
    }

    const toLabel = (value) => {
      const dt = new Date(value)
      if (Number.isNaN(dt.getTime())) return ""
      return dt.toISOString().slice(0, 10)
    }

    return {
      enabled: true,
      startDateLabel: toLabel(probationStartDate),
      endDateLabel: toLabel(probationEndDate),
      message: "",
    }
  }, [
    welfareProbationUnit,
    welfareProbationValue,
    welfareEnrollmentEnabled,
    welfareEnrollmentEndDate,
    minDate,
  ])
  const welfareCoverParentsEnabled = !!welfareCoverParents
  const welfareCoverSpouseEnabled = !!welfareCoverSpouse
  const welfareCoverSpouseParentsEnabled = !!welfareCoverSpouseParents && welfareCoverSpouseEnabled
  const welfareCoverChildrenEnabled = !!welfareCoverChildren
  const welfareCoverSiblingsEnabled = !!welfareCoverSiblings
  const allCoveredRelationsEnabled =
    welfareCoverParentsEnabled &&
    welfareCoverSpouseEnabled &&
    welfareCoverSpouseParentsEnabled &&
    welfareCoverChildrenEnabled &&
    welfareCoverSiblingsEnabled

  const handleToggleAllCoveredRelations = () => {
    const nextEnabled = !allCoveredRelationsEnabled
    onChangeWelfareCoverParents?.(nextEnabled)
    onChangeWelfareCoverChildren?.(nextEnabled)
    onChangeWelfareCoverSiblings?.(nextEnabled)
    if (nextEnabled) {
      onChangeWelfareCoverSpouse?.(true)
      onChangeWelfareCoverSpouseParents?.(true)
      return
    }
    onChangeWelfareCoverSpouseParents?.(false)
    onChangeWelfareCoverSpouse?.(false)
  }

  const chamaaTheme = GROUP_THEMES[GROUP_TYPES.CHAMAA]
  const savingsTheme = GROUP_THEMES[GROUP_TYPES.SAVINGS]
  const welfareTheme = GROUP_THEMES[GROUP_TYPES.WELFARE]
  const nonRecurringTheme = GROUP_THEMES[GROUP_TYPES.NON_RECURRING]
  const otherTheme = GROUP_THEMES[GROUP_TYPES.OTHER]
  const selectedTheme = isChamaaSelected
    ? chamaaTheme
    : isProjectSavingsSelected
      ? savingsTheme
      : isWelfareSelected
        ? welfareTheme
        : otherTheme
  const showValidationState = invalidAttemptCount > 0
  const showInvalid = (value) => showValidationState && !!value
  const invalidShakeClass =
    invalidAttemptCount > 0
      ? invalidAttemptCount % 2 === 0
        ? "animate-field-shake-a"
        : "animate-field-shake-b"
      : ""
  const getInvalidFieldClass = (isInvalid) => (
    showInvalid(isInvalid)
      ? `border-red-300 bg-red-50/40 focus:ring-red-200 ${invalidShakeClass}`
      : "border-gray-300"
  )

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Step 1: Select Event Category
        </label>
        <p className="text-[11px] text-gray-500 mb-3">
          Choose whether this is a one-time or recurring event.
        </p>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${
            showInvalid(validation.eventCategory) ? invalidShakeClass : ""
          }`}
        >
          <button
            type="button"
            onClick={() => onChangeCategory("non-recurring")}
            className={`flex items-center justify-between gap-3 px-4 py-3.5 border-2 rounded-xl text-sm font-medium transition-all ${category === "non-recurring"
              ? `border-${nonRecurringTheme.primary} ${nonRecurringTheme.bg} ${nonRecurringTheme.text} shadow-sm`
              : "border-gray-200 bg-white text-gray-700 hover:border-[#1F7DFF] hover:bg-[#1F7DFF]/40"
              }`}
          >
            <div className="flex items-center gap-2.5">
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${category === "non-recurring" ? `${nonRecurringTheme.button}` : "bg-gray-100 text-gray-500"
                  }`}
              >
                <Icon name="calendar" size="1rem" />
              </div>
              <span className="font-semibold">Event Group</span>
              <span className="text-[11px] font-normal text-gray-500 block">(One Time Event)</span>
            </div>
            {category === "non-recurring" && (
              <Icon name="check-circle" size="1.1rem" className={`${nonRecurringTheme.text} flex-shrink-0`} />
            )}
          </button>

          <button
            type="button"
            onClick={() => onChangeCategory("recurring")}
            className={`flex items-center justify-between gap-3 px-4 py-3.5 border-2 rounded-xl text-sm font-medium transition-all ${category === "recurring"
              ? `border-${selectedTheme.primary} ${selectedTheme.bg} ${selectedTheme.text} shadow-sm`
              : "border-gray-200 bg-white text-gray-700 hover:border-sky-200 hover:bg-sky-50/50"
              }`}
          >
            <div className="flex items-center gap-2.5">
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${category === "recurring" ? `${selectedTheme.button}` : "bg-gray-100 text-gray-500"
                  }`}
              >
                <Icon name="repeat" size="1rem" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-semibold">Recurring Cycle Group</span>
                <span className="text-start text-[11px] font-normal text-gray-500">(Short-term group)</span>
              </div>
            </div>
            {category === "recurring" && (
              <Icon name="check-circle" size="1.1rem" className={`${selectedTheme.text} flex-shrink-0`} />
            )}
          </button>
        </div>
        {showInvalid(validation.eventCategory) && (
          <p className="text-[11px] text-red-600 mt-2">Select an event category to continue.</p>
        )}
      </div>

      {category === "non-recurring" && (
        <div className={`${nonRecurringTheme.bgMuted50} border ${nonRecurringTheme.border} rounded-xl p-4 space-y-4`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Non Recurring Event
            </label>
            <div className={`space-y-2 ${showInvalid(validation.nonRecurringEvent) ? invalidShakeClass : ""}`}>
              {NON_RECURRING_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => onChangeNonRecurring(option)}
                  className={`w-full flex items-center justify-between px-3 py-3 border rounded-xl text-sm font-medium transition-all ${nonRecurringValue === option
                    ? `${nonRecurringTheme.border} bg-white ${nonRecurringTheme.text} shadow-sm ring-1 ring-[#1F7DFF]`
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                    }`}
                >
                  <span>{option}</span>
                  {nonRecurringValue === option && (
                    <Icon name="check" size="1rem" className={`${nonRecurringTheme.text}`} />
                  )}
                </button>
              ))}
            </div>
            {showInvalid(validation.nonRecurringEvent) && (
              <p className="text-[11px] text-red-600 mt-2">Select a non-recurring event type.</p>
            )}
          </div>

          {nonRecurringValue === "Other" && (
            <div 
              ref={nonRecurringRef} 
              className="animate-in fade-in slide-in-from-top-2 duration-500"
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe Non Recurring Event (Other)
              </label>
              <textarea
                value={otherDescription}
                onChange={(e) => onChangeOther(e.target.value)}
                rows={3}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${nonRecurringTheme.ring} ${
                  showInvalid(validation.nonRecurringOtherDescription)
                    ? `border-red-300 bg-red-50/40 focus:ring-red-200 ${invalidShakeClass}`
                    : "border-gray-300 bg-white"
                }`}
                placeholder="Describe your non-recurring event..."
              />
              {showInvalid(validation.nonRecurringOtherDescription) && (
                <p className="text-[11px] text-red-600 mt-1.5">Description is required.</p>
              )}
            </div>
          )}
        </div>
      )}

      {category === "recurring" && (
        <div className={`${selectedTheme.bgMuted50} border ${selectedTheme.border} rounded-xl p-4 space-y-4`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Recurring Event
              <span className="text-[11px] font-normal text-gray-500 block">Membership required</span> 
            </label>
            <div className={`space-y-2 ${showInvalid(validation.recurringEvent) ? invalidShakeClass : ""}`}>
              {RECURRING_OPTIONS.map((option) => {
                const isChamaa = option === CHAMAA_LABEL
                const isSavings = option === PROJECT_SAVINGS_LABEL
                const isWelfare =
                  option === WELFARE_LABEL ||
                  option.toLowerCase().includes("welfare") ||
                  option.toLowerCase().includes("support")
                const optTheme = isChamaa
                  ? chamaaTheme
                  : isSavings
                    ? savingsTheme
                    : isWelfare
                      ? welfareTheme
                      : otherTheme
                
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onChangeRecurring(option)}
                    className={`w-full flex items-center justify-between px-3 py-3 border rounded-xl text-sm font-medium transition-all ${recurringValue === option
                      ? `${optTheme.border} bg-white ${optTheme.text} shadow-sm ring-1 ${optTheme.ring.replace('focus:', '')}`
                      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                  >
                    <span>{option}</span>
                    {recurringValue === option && (
                      <Icon name="check" size="1rem" className={`${optTheme.text}`} />
                    )}
                  </button>
                )
              })}
            </div>
            {showInvalid(validation.recurringEvent) && (
              <p className="text-[11px] text-red-600 mt-2">Select a recurring event type.</p>
            )}
          </div>

          {recurringValue && (
            <div 
              ref={recurringRef} 
              className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-500"
            >
              {recurringValue === "Others" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Describe Recurring Event (Others)
                  </label>
                  <textarea
                    value={otherDescription}
                    onChange={(e) => onChangeOther(e.target.value)}
                    rows={3}
                    className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} ${
                      showInvalid(validation.recurringOtherDescription)
                        ? `border-red-300 bg-red-50/40 focus:ring-red-200 ${invalidShakeClass}`
                        : "border-gray-300 bg-white"
                    }`}
                    placeholder="Describe your recurring event..."
                  />
                  {showInvalid(validation.recurringOtherDescription) && (
                    <p className="text-[11px] text-red-600 mt-1.5">Description is required.</p>
                  )}
                </div>
              )}

              <div className={`border-t ${selectedTheme.border} pt-4 space-y-4`}>
                {isChamaaSelected ? (
                  <div className={`bg-white/80 border ${selectedTheme.border} rounded-xl p-3 space-y-3`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contribution per member (KSh)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={longAmount}
                        onChange={(e) => onChangeLongAmount?.(e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                          validation.chamaaContribution
                        )}`}
                        placeholder="e.g., 2000"
                      />
                      {showInvalid(validation.chamaaContribution) && (
                        <p className="text-[11px] text-red-600 mt-1.5">Contribution amount is required.</p>
                      )}
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        This amount will be used in the first cycle created with this group.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max shares per person
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={chamaaMaxSharesPerMember ?? "1"}
                        onChange={(e) => onChangeChamaaMaxSharesPerMember?.(e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                          validation.chamaaMaxSharesPerMember
                        )}`}
                        placeholder="e.g., 3"
                      />
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        Members can choose between 1 and this maximum during enrollment.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Late Payment Fee (KSh)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={lateFee}
                        onChange={(e) => onChangeLateFee?.(e.target.value)}
                        className={`w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white`}
                        placeholder="0"
                      />
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        This becomes the group default and carries into future cycles until changed.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First cycle start date
                      </label>
                      <input
                        type="date"
                        value={chamaaFirstCycleStartDate || ""}
                        onChange={(e) => onChangeChamaaFirstCycleStartDate?.(e.target.value)}
                        min={minDate}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                          validation.chamaaFirstCycleStartDate
                        )}`}
                      />
                      {showInvalid(validation.chamaaFirstCycleStartDate) && (
                        <p className="text-[11px] text-red-600 mt-1.5">First cycle start date is required.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First cycle invoice frequency
                      </label>
                      <select
                        value={chamaaFirstCycleInvoiceFrequency || ""}
                        onChange={(e) => onChangeChamaaFirstCycleInvoiceFrequency?.(e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                          validation.chamaaFirstCycleInvoiceFrequency
                        )}`}
                      >
                        <option value="">Select frequency...</option>
                        {CHAMAA_CYCLE_FREQUENCIES.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {showInvalid(validation.chamaaFirstCycleInvoiceFrequency) && (
                        <p className="text-[11px] text-red-600 mt-1.5">First cycle invoice frequency is required.</p>
                      )}
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        Members can pay each invoice until the next invoice date.
                      </p>
                    </div>
                  </div>
                ) : isProjectSavingsSelected ? (
                  <div className={`bg-white/80 border ${selectedTheme.border} rounded-xl p-3 space-y-3`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Per person amount (KSh)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={projectSavingsPerPersonAmount}
                        onChange={(e) => onChangeProjectSavingsPerPersonAmount?.(e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                          validation.projectSavingsPerPersonAmount
                        )}`}
                        placeholder="e.g., 6000"
                      />
                      {showInvalid(validation.projectSavingsPerPersonAmount) && (
                        <p className="text-[11px] text-red-600 mt-1.5">Per person amount is required.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Closing date
                      </label>
                      <input
                        type="date"
                        value={projectSavingsClosingDate || ""}
                        onChange={(e) => onChangeProjectSavingsClosingDate?.(e.target.value)}
                        min={minDate}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                          validation.projectSavingsClosingDate
                        )}`}
                      />
                      {showInvalid(validation.projectSavingsClosingDate) && (
                        <p className="text-[11px] text-red-600 mt-1.5">Closing date is required.</p>
                      )}
                    </div>
                    <div className={`rounded-lg border ${selectedTheme.border} ${selectedTheme.bg}/70 px-3 py-2`}>
                      <p className={`text-[11px] ${selectedTheme.text}`}>
                        Monthly amount (info only):{" "}
                        <span className="font-semibold">
                          {projectSavingsMonthlyAmount > 0
                            ? `${projectSavingsMonthlyAmount.toFixed(2)} KSh / month`
                            : "-"}
                        </span>
                      </p>
                      <p className="text-10px text-gray-500 mt-1">
                        Calculated from today to closing date for each member.
                      </p>
                    </div>
                  </div>
                ) : isWelfareSelected ? (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className={`rounded-xl border ${selectedTheme.border} ${selectedTheme.mutedBg} p-3 space-y-3`}>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-700">Enrollment enabled</label>
                        <button
                          type="button"
                          onClick={() => onChangeWelfareEnrollmentEnabled?.(!welfareEnrollmentEnabled)}
                          className={`relative inline-flex h-6 w-14 items-center rounded-full border transition-all shadow-sm ${
                            welfareEnrollmentEnabled
                              ? "bg-[#3b82f6] border-[#3b82f6]"
                              : "bg-gray-200 border-gray-300"
                          }`}
                        >
                          <span
                            className={`absolute text-[9px] font-black tracking-tight ${
                              welfareEnrollmentEnabled ? "left-2 text-white" : "right-2 text-gray-500"
                            }`}
                          >
                            {welfareEnrollmentEnabled ? "ON" : "OFF"}
                          </span>
                          <span
                            className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-300 ease-in-out ${
                              welfareEnrollmentEnabled ? "translate-x-8" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-medium text-gray-700 mb-1">Start date</label>
                          <input
                            type="date"
                            value={welfareEnrollmentStartDate || ""}
                            onChange={(e) => onChangeWelfareEnrollmentStartDate?.(e.target.value)}
                            min={minDate}
                            className={`w-full px-3 py-2 border rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-${selectedTheme.primary} ${getInvalidFieldClass(
                              validation.welfareEnrollmentStartDate
                            )}`}
                          />
                          {showInvalid(validation.welfareEnrollmentStartDate) && (
                            <p className="text-[11px] text-red-600 mt-1">Start date is required.</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-700 mb-1">End date (optional)</label>
                          <input
                            type="date"
                            value={welfareEnrollmentEndDate || ""}
                            onChange={(e) => onChangeWelfareEnrollmentEndDate?.(e.target.value)}
                            min={welfareEnrollmentStartDate || minDate}
                            className={`w-full px-3 py-2 border rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-${selectedTheme.primary} ${getInvalidFieldClass(
                              validation.welfareEnrollmentEndDate
                            )}`}
                          />
                          {showInvalid(validation.welfareEnrollmentEndDate) && (
                            <p className="text-[11px] text-red-600 mt-1">
                              End date cannot be earlier than start date.
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="block text-[11px] font-medium text-gray-700 mb-1">Registration Fee (KSh)</label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={welfareRegistrationFee || ""}
                            onChange={(e) => onChangeWelfareRegistrationFee?.(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-${selectedTheme.primary} ${getInvalidFieldClass(
                              validation.welfareRegistrationFee
                            )}`}
                          />
                          {showInvalid(validation.welfareRegistrationFee) && (
                            <p className="text-[11px] text-red-600 mt-1">Registration fee is required.</p>
                          )}
                        </div>
                      </div>

                      <div className="pt-2 border-t border-gray-200/80 space-y-2">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Shield size={14} />
                          <p className="text-xs font-semibold">Probation</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-700 mb-1">Duration</label>
                            <select
                              value={welfareProbationUnit || ""}
                              onChange={(e) => onChangeWelfareProbationUnit?.(e.target.value)}
                              className={`w-full px-3 py-2 border rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-${selectedTheme.primary} ${getInvalidFieldClass(
                                validation.welfareProbationUnit
                              )}`}
                            >
                              <option value="">No probation</option>
                              <option value="days">Days</option>
                              <option value="months">Months</option>
                            </select>
                            {showInvalid(validation.welfareProbationUnit) && (
                              <p className="text-[11px] text-red-600 mt-1">Select probation duration unit.</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-700 mb-1">Length</label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={welfareProbationValue || ""}
                              onChange={(e) => onChangeWelfareProbationValue?.(e.target.value)}
                              disabled={!["days", "months"].includes(String(welfareProbationUnit || "").toLowerCase())}
                              className={`w-full px-3 py-2 border rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-${selectedTheme.primary} ${getInvalidFieldClass(
                                validation.welfareProbationValue
                              )}`}
                              placeholder="e.g. 30"
                            />
                            {showInvalid(validation.welfareProbationValue) && (
                              <p className="text-[11px] text-red-600 mt-1">Enter a valid probation length.</p>
                            )}
                          </div>
                        </div>
                        {welfareProbationPreview.enabled && welfareProbationPreview.startDateLabel && welfareProbationPreview.endDateLabel ? (
                          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
                            <p>
                              Start date: <span className="font-semibold">{welfareProbationPreview.startDateLabel}</span>
                            </p>
                            <p>
                              End date: <span className="font-semibold">{welfareProbationPreview.endDateLabel}</span>
                            </p>
                          </div>
                        ) : (
                          <p className="text-[11px] text-gray-500">{welfareProbationPreview.message}</p>
                        )}
                      </div>
                    </div>

                    <div className={`bg-gradient-to-br from-${selectedTheme.bg} to-white border ${selectedTheme.border} rounded-2xl p-5 shadow-sm`}>
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-white border ${selectedTheme.border} shadow-sm`}>
                            <Users className={`w-5 h-5 text-${selectedTheme.text}`} />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Covered Relations</h4>
                            <p className="text-xs text-gray-500">Define eligible family members</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleToggleAllCoveredRelations}
                          className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 hover:bg-gray-50"
                        >
                          <div className={`relative inline-flex h-6 w-14 items-center rounded-full border transition-all shadow-sm ${
                            allCoveredRelationsEnabled
                              ? "bg-[#3b82f6] border-[#3b82f6]"
                              : "bg-gray-200 border-gray-300"
                          }`}>
                            <span
                              className={`absolute text-[9px] font-black tracking-tight ${
                                allCoveredRelationsEnabled ? "left-2 text-white" : "right-2 text-gray-500"
                              }`}
                            >
                              {allCoveredRelationsEnabled ? "ON" : "OFF"}
                            </span>
                            <span
                              className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-300 ease-in-out ${
                                allCoveredRelationsEnabled ? "translate-x-8" : "translate-x-0"
                              }`}
                            />
                          </div>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { key: "spouse", label: "Spouse", enabled: welfareCoverSpouseEnabled, onToggle: () => onChangeWelfareCoverSpouse?.(!welfareCoverSpouseEnabled) },
                          { key: "parents", label: "Parents", enabled: welfareCoverParentsEnabled, onToggle: () => onChangeWelfareCoverParents?.(!welfareCoverParentsEnabled) },
                          { key: "spouseParents", label: "Spouse Parents", enabled: welfareCoverSpouseParentsEnabled, disabled: !welfareCoverSpouseEnabled, onToggle: () => onChangeWelfareCoverSpouseParents?.(!welfareCoverSpouseParentsEnabled) },
                          { key: "children", label: "Children", enabled: welfareCoverChildrenEnabled, onToggle: () => onChangeWelfareCoverChildren?.(!welfareCoverChildrenEnabled) },
                          { key: "siblings", label: "Siblings", enabled: welfareCoverSiblingsEnabled, onToggle: () => onChangeWelfareCoverSiblings?.(!welfareCoverSiblingsEnabled) },
                        ].map((row) => (
                          <button
                            key={row.key}
                            type="button"
                            disabled={row.disabled}
                            onClick={row.onToggle}
                            className={`flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                              row.disabled ? "opacity-50 cursor-not-allowed bg-gray-50 border-gray-200" :
                              row.enabled ? `${selectedTheme.bg} border-${selectedTheme.primary} ring-1 ring-${selectedTheme.primary}` : "bg-white border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <span className={`text-sm font-medium ${row.enabled ? selectedTheme.text : "text-gray-700"}`}>
                              {row.label}
                            </span>
                            <div className={`relative inline-flex h-6 w-14 items-center rounded-full border transition-all shadow-sm ${
                              row.enabled ? "bg-[#3b82f6] border-[#3b82f6]" : "bg-gray-200 border-gray-300"
                            }`}>
                              <span
                                className={`absolute text-[9px] font-black tracking-tight ${
                                  row.enabled ? "left-2 text-white" : "right-2 text-gray-500"
                                }`}
                              >
                                {row.enabled ? "ON" : "OFF"}
                              </span>
                              <span
                                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-300 ease-in-out ${
                                  row.enabled ? "translate-x-8" : "translate-x-0"
                                }`}
                              />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div
                      className={`bg-gradient-to-br from-${selectedTheme.bg} to-white border ${selectedTheme.border} rounded-2xl p-5 shadow-sm ${
                        showInvalid(validation.welfareBenefitAmounts || validation.welfareReimbursementAmounts)
                          ? `ring-2 ring-red-200 ${invalidShakeClass}`
                          : ""
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-5">
                        <div className={`p-2 rounded-lg bg-white border ${selectedTheme.border} shadow-sm`}>
                          <Shield className={`w-5 h-5 text-${selectedTheme.text}`} />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Eligible amount and Reimbursement Amounts</h4>
                          <p className="text-xs text-gray-500">Fixed amounts per relationship</p>
                          {showInvalid(validation.welfareBenefitAmounts || validation.welfareReimbursementAmounts) && (
                            <p className="text-[11px] text-red-600 mt-1">Values must be 0 or greater.</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {[
                          {
                            label: "Member",
                            enabled: true,
                            lossReportAmountValue: welfareBenefitMember,
                            onLossReportAmountChange: onChangeWelfareBenefitMember,
                            reimbursementValue: welfareReimbursementMember,
                            onReimbursementChange: onChangeWelfareReimbursementMember,
                          },
                          {
                            label: "Spouse",
                            enabled: welfareCoverSpouseEnabled,
                            lossReportAmountValue: welfareBenefitSpouse,
                            onLossReportAmountChange: onChangeWelfareBenefitSpouse,
                            reimbursementValue: welfareReimbursementSpouse,
                            onReimbursementChange: onChangeWelfareReimbursementSpouse,
                          },
                          {
                            label: "Parent",
                            enabled: welfareCoverParentsEnabled,
                            lossReportAmountValue: welfareBenefitParent,
                            onLossReportAmountChange: onChangeWelfareBenefitParent,
                            reimbursementValue: welfareReimbursementParent,
                            onReimbursementChange: onChangeWelfareReimbursementParent,
                          },
                          {
                            label: "Spouse Parent",
                            enabled: welfareCoverSpouseEnabled && welfareCoverSpouseParentsEnabled,
                            lossReportAmountValue: welfareBenefitSpouseParent,
                            onLossReportAmountChange: onChangeWelfareBenefitSpouseParent,
                            reimbursementValue: welfareReimbursementSpouseParent,
                            onReimbursementChange: onChangeWelfareReimbursementSpouseParent,
                          },
                          {
                            label: "Child",
                            enabled: welfareCoverChildrenEnabled,
                            lossReportAmountValue: welfareBenefitChild,
                            onLossReportAmountChange: onChangeWelfareBenefitChild,
                            reimbursementValue: welfareReimbursementChild,
                            onReimbursementChange: onChangeWelfareReimbursementChild,
                          },
                          {
                            label: "Sibling",
                            enabled: welfareCoverSiblingsEnabled,
                            lossReportAmountValue: welfareBenefitSibling,
                            onLossReportAmountChange: onChangeWelfareBenefitSibling,
                            reimbursementValue: welfareReimbursementSibling,
                            onReimbursementChange: onChangeWelfareReimbursementSibling,
                          },
                        ].map((row) => (
                          <div
                            key={row.label}
                            className={`grid grid-cols-12 gap-2 items-center bg-white p-2.5 rounded-xl border border-gray-200 transition-shadow ${
                              row.enabled
                                ? `focus-within:ring-2 focus-within:ring-${selectedTheme.primary}`
                                : "opacity-60"
                            }`}
                          >
                            <div className="col-span-12 sm:col-span-4">
                              <div className="flex items-center justify-between">
                                <label className="block text-[11px] font-semibold text-gray-700">
                                  {row.label}
                                </label>
                                <Pencil className="w-3 h-3 text-gray-400" />
                              </div>
                            </div>
                            <div className="col-span-6 sm:col-span-4">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.lossReportAmountValue || ""}
                                onChange={(e) => row.onLossReportAmountChange?.(e.target.value)}
                                disabled={!row.enabled}
                                className="w-full text-sm font-medium text-gray-900 focus:outline-none bg-gray-50/50 rounded px-2 py-1.5 border border-gray-100 placeholder-gray-400 placeholder:text-[9.24px]"
                                placeholder="Eligible amount"
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-4">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.reimbursementValue || ""}
                                onChange={(e) => row.onReimbursementChange?.(e.target.value)}
                                disabled={!row.enabled}
                                className="w-full text-sm font-medium text-gray-900 focus:outline-none bg-gray-50/50 rounded px-2 py-1.5 border border-gray-100 placeholder-gray-400 placeholder:text-[9.24px]"
                                placeholder="Reimbursement"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => onChangeAutoInvoicing(!autoInvoicing)}
                        className={`relative inline-flex h-6 w-14 items-center rounded-full border transition-all shadow-sm ${
                          autoInvoicing ? "bg-[#3b82f6] border-[#3b82f6]" : "bg-gray-200 border-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute text-[9px] font-black tracking-tight ${
                            autoInvoicing ? "left-2 text-white" : "right-2 text-gray-500"
                          }`}
                        >
                          {autoInvoicing ? "ON" : "OFF"}
                        </span>
                        <span
                          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-300 ease-in-out ${
                            autoInvoicing ? "translate-x-8" : "translate-x-0"
                          }`}
                        />
                      </button>
                      <div className="flex-1">
                        <label className="block text-sm font-semibold text-gray-800 mb-0.5">
                          Auto Invoicing
                        </label>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {autoInvoicing
                            ? "Invoices will be sent automatically to all group members at the selected frequency."
                            : "You will manually send invoices to group members."}
                        </p>
                      </div>
                    </div>

                    {autoInvoicing && (
                      <div className={`bg-white/80 border ${selectedTheme.border} rounded-xl p-3 space-y-3`}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Auto invoice frequency
                          </label>
                          <select
                            value={invoiceFrequency}
                            onChange={(e) => {
                              const value = e.target.value
                              onChangeInvoiceFrequency(value)
                              onChangeLongFrequency?.(value)
                            }}
                            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                              validation.autoInvoiceFrequency
                            )}`}
                          >
                            <option value="">Select frequency...</option>
                            {INVOICE_FREQUENCY_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          {showInvalid(validation.autoInvoiceFrequency) && (
                            <p className="text-[11px] text-red-600 mt-1.5">Invoice frequency is required.</p>
                          )}
                          <p className="text-[11px] text-gray-500 mt-1.5">
                            Invoices will be automatically generated and sent at this frequency.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Start Date for Invoicing
                          </label>
                          <input
                            type="date"
                            value={invoiceStartDate}
                            onChange={(e) => onChangeInvoiceStartDate(e.target.value)}
                            min={minDate}
                            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white ${getInvalidFieldClass(
                              validation.autoInvoiceStartDate
                            )}`}
                          />
                          {showInvalid(validation.autoInvoiceStartDate) && (
                            <p className="text-[11px] text-red-600 mt-1.5">Start date is required.</p>
                          )}
                          <p className="text-[11px] text-gray-500 mt-1.5">
                            The first invoice will be sent on this date. Subsequent invoices will follow the selected frequency.
                          </p>
                        </div>

                        {!isOnDemandFrequency && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Set amount (KSh)
                            </label>
                            <input
                              type="number"
                              value={longAmount}
                              onChange={(e) => onChangeLongAmount?.(e.target.value)}
                              className={`w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white`}
                              placeholder="e.g., 2000"
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Late Payment Fee (KSh)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={lateFee}
                            onChange={(e) => onChangeLateFee?.(e.target.value)}
                            className={`w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white`}
                            placeholder="0"
                          />
                          <p className="mt-1.5 text-[11px] text-gray-500">
                            Added once when a member's invoice becomes overdue.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient (admin-selected)
                          </label>
                          <select
                            value={recipient}
                            onChange={(e) => onChangeRecipient?.(e.target.value)}
                            className={`w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 ${selectedTheme.ring} bg-white`}
                          >
                            <option value="">Choose recipient...</option>
                            {RECIPIENT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default StepEventType
